import React, { useState, useEffect } from 'react';
import { Calendar, Leaf, Sun, CloudRain, Snowflake, Save, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Toggle } from './ui/toggle';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const HEMISPHERE_DEFAULTS = {
  southern: { spring: 9, summer: 12, autumn: 3, winter: 6 },
  northern: { spring: 3, summer: 6, autumn: 9, winter: 12 },
};

const SEASONS = [
  { key: 'spring', label: 'Spring', icon: Leaf, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' },
  { key: 'summer', label: 'Summer', icon: Sun, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
  { key: 'autumn', label: 'Autumn', icon: CloudRain, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' },
  { key: 'winter', label: 'Winter', icon: Snowflake, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
];

const SeasonSettings = () => {
  const [hemisphere, setHemisphere] = useState('southern');
  const [seasonStarts, setSeasonStarts] = useState({ ...HEMISPHERE_DEFAULTS.southern });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API}/season-settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const data = res.data;
        if (data.hemisphere) setHemisphere(data.hemisphere);
        if (data.season_starts) setSeasonStarts(data.season_starts);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const switchHemisphere = (h) => {
    setHemisphere(h);
    setSeasonStarts({ ...HEMISPHERE_DEFAULTS[h] });
  };

  const resetToDefaults = () => {
    setSeasonStarts({ ...HEMISPHERE_DEFAULTS[hemisphere] });
    toast.info(`Reset to ${hemisphere === 'southern' ? 'Southern' : 'Northern'} defaults`);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/season-settings`, {
        hemisphere,
        season_starts: seasonStarts,
      }, { headers: { Authorization: `Bearer ${token}` } });
      // Also update localStorage for immediate use by SeasonalReminders
      localStorage.setItem('hemisphere', hemisphere);
      toast.success('Season settings saved');
    } catch {
      toast.error('Failed to save settings');
    }
    setSaving(false);
  };

  // Determine current season based on settings
  const currentMonth = new Date().getMonth() + 1;
  const getCurrentSeason = () => {
    const ordered = Object.entries(seasonStarts).sort((a, b) => a[1] - b[1]);
    let current = ordered[0][0];
    for (const [s, m] of ordered) {
      if (currentMonth >= m) current = s;
    }
    return current;
  };
  const currentSeason = getCurrentSeason();
  const currentMeta = SEASONS.find(s => s.key === currentSeason) || SEASONS[0];
  const CurrentIcon = currentMeta.icon;

  if (!loaded) return null;

  return (
    <div className="space-y-6" data-testid="season-settings">
      <div>
        <h2 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
          <Calendar className="h-6 w-6 text-amber-600" />
          Season Settings
        </h2>
        <p className="text-sm text-stone-500 mt-1">Configure your seasonal calendar. This controls when each season starts for your checklists and reminders.</p>
      </div>

      {/* Hemisphere Selection */}
      <Card>
        <CardContent className="p-5">
          <h3 className="font-semibold text-stone-700 mb-3">Hemisphere</h3>
          <div className="flex items-center gap-3">
            <Toggle
              pressed={hemisphere === 'southern'}
              onPressedChange={() => switchHemisphere('southern')}
              className="h-10 px-5 text-sm font-medium data-[state=on]:bg-stone-800 data-[state=on]:text-white"
              data-testid="settings-hemisphere-southern"
            >
              Southern
            </Toggle>
            <Toggle
              pressed={hemisphere === 'northern'}
              onPressedChange={() => switchHemisphere('northern')}
              className="h-10 px-5 text-sm font-medium data-[state=on]:bg-stone-800 data-[state=on]:text-white"
              data-testid="settings-hemisphere-northern"
            >
              Northern
            </Toggle>
            <span className="text-sm text-stone-400 ml-2">
              {hemisphere === 'southern' ? 'Australia, NZ, South America, South Africa' : 'North America, Europe, Asia'}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Season Start Months */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-stone-700">Season Start Months</h3>
            <button
              onClick={resetToDefaults}
              className="text-xs text-stone-400 hover:text-stone-600 flex items-center gap-1"
              data-testid="reset-defaults-btn"
            >
              <RefreshCw className="h-3 w-3" />
              Reset to defaults
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SEASONS.map(({ key, label, icon: Icon, color, bg, border }) => (
              <div key={key} className={`flex items-center gap-3 p-3 rounded-xl ${bg} border ${border}`}>
                <Icon className={`h-5 w-5 ${color} flex-shrink-0`} />
                <span className={`font-medium text-sm ${color} w-20`}>{label}</span>
                <Select
                  value={String(seasonStarts[key])}
                  onValueChange={(val) => setSeasonStarts(prev => ({ ...prev, [key]: parseInt(val) }))}
                >
                  <SelectTrigger className="flex-1 h-9 bg-white" data-testid={`season-start-${key}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map(m => (
                      <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>

          <p className="text-xs text-stone-400 mt-3">Adjust the start month of each season to match your local climate. Each season runs until the next one begins.</p>
        </CardContent>
      </Card>

      {/* Current Season Preview */}
      <Card className={`${currentMeta.bg} ${currentMeta.border}`}>
        <CardContent className="p-5">
          <div className="flex items-center gap-2">
            <CurrentIcon className={`h-5 w-5 ${currentMeta.color}`} />
            <span className={`font-semibold ${currentMeta.color}`}>Current Season: {currentMeta.label}</span>
            <span className="text-xs text-stone-500">(based on your settings, {MONTHS[currentMonth - 1].label} is in {currentMeta.label})</span>
          </div>
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-amber-600 hover:bg-amber-700 text-white px-8"
          data-testid="save-season-settings-btn"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

export default SeasonSettings;
